import { memo, useRef } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';

import { useClinicalNoteContext } from 'lib/context/ClinicalNoteContext/ClinicalNoteContext';

import { clinicalNote as clinicalNoteState } from 'components/state';

import { handleEnhanceNarrativeFactory } from '../../../CurrentNote/TypeNotes/lib/helpers';

const AIForm = ({
  component,
  inputName,
  components = {},
  noteType,
  formNameMap,
  preferredFormNameMap,
  overrides,
  isScrolling,
  ...rest
}) => {
  const Component = component;

  const { patient, hpNoteOverviewRefs } = useOutletContext();

  const clinicalNote = useRecoilValue(clinicalNoteState);

  const queryClient = useQueryClient();

  const {
    currentHpOverviewData,
    setOverviewData,
    setCurrentHpOverviewData,
    setSaveButtonText,
    updateSaveButton,
    currentHpOverviewLoading,
    setEnhanceNarrativeParams
  } = useClinicalNoteContext();

  const { id, appointmentId } = useParams();

  const sectionRef = useRef();

  const handleEnhanceNarrative = handleEnhanceNarrativeFactory({
    type: noteType,
    formName: inputName,
    components,
    sectionRef,
    clinicalNote,
    currentHpOverviewData,
    mappings: formNameMap,
    preferredMappings: preferredFormNameMap,
    overrides,
    extraBodyResolvers:
      noteType === 'hp'
        ? [
            async ({ name }) => {
              return queryClient.getQueryData(['patientAggregateNarrative', id, name])?.data
                ?.narrative?.narrative;
            }
          ]
        : [],
    setEnhanceNarrativeParams
  });

  const customFormsParams = {
    id,
    appointmentId,
    setOverviewData,
    setCurrentHpOverviewData,
    setSaveButtonText,
    updateSaveButton,
    currentHpOverviewLoading
  };

  return Component ? (
    <Component
      noteType={noteType}
      context={{ patient }}
      components={components}
      sectionRef={sectionRef}
      options={[
        {
          title: 'Enhance with MyScribe AI',
          group: 'MyScribe AI',
          icon: 'myscribeai-filled',
          onClick: handleEnhanceNarrative,
          id: 'myscribe-enhance-narrative',
          permission: {
            isAny: true,
            requiredPermissions: ['is_admin', 'myscribe.narrative_update']
          }
        }
      ]}
      formId={currentHpOverviewData?.[inputName]?.id}
      hpNoteOverviewRef={hpNoteOverviewRefs?.[inputName]}
      customFormsParams={customFormsParams}
      setCurrentFormData={(newData) =>
        setCurrentHpOverviewData((prevState) => ({
          ...prevState,
          [inputName]: { ...(prevState?.[inputName] || {}), response: newData }
        }))
      }
      isScrolling={isScrolling}
      {...rest}
    />
  ) : null;
};

export default memo(AIForm);
